import React, { type ReactElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import { NavLink } from 'reactstrap';
import TooltipItem from 'atom-components/tooltip-item';
import RestrictedNav from 'feature-components/routes/restricted-nav';
import { MAIN_MENU_ITEMS, type MainMenuItem } from 'feature-components/sidebar/main-menu-items';

type MenuItemProps = {
  menuItemState: {
    menuItem: MainMenuItem;
    menuExpanded: boolean;
  };
};

function MenuItemNavLink({ menuItemState }: MenuItemProps): ReactElement {
  const { menuItem, menuExpanded } = menuItemState;
  const { pathname } = useLocation();

  return (
    <NavLink
      to={menuItem.to}
      tag={Link}
      active={menuItem.isActive(pathname)}
      className={clsx({ 'justify-content-center': !menuExpanded })}
    >
      <FontAwesomeIcon
        icon={menuItem.icon}
        className={clsx('fa-fw', { 'me-3 icon': menuExpanded })}
        size="lg"
      />
      {menuExpanded && <>{menuItem.label}</>}
    </NavLink>
  );
}

function MenuItem({ menuItemState }: MenuItemProps): ReactElement {
  const { menuItem, menuExpanded } = menuItemState;
  return (
    <li key={menuItem.to} className="nav-item w-100">
      {menuExpanded ? (
        <MenuItemNavLink menuItemState={menuItemState} />
      ) : (
        <TooltipItem
          id={menuItem.label.replaceAll(' ', '-')}
          tooltip={menuItem.label}
          tooltipProps={{ placement: 'right' }}
        >
          <MenuItemNavLink menuItemState={menuItemState} />
        </TooltipItem>
      )}
    </li>
  );
}

function MenuItemAuthoriser({ menuItemState }: MenuItemProps): ReactElement {
  const { menuItem } = menuItemState;
  if (menuItem.authorisableRoute) {
    return (
      <RestrictedNav key={menuItem.to} route={menuItem.authorisableRoute}>
        <MenuItem menuItemState={menuItemState} />
      </RestrictedNav>
    );
  }
  return <MenuItem menuItemState={menuItemState} />;
}

type MainMenuProps = { expanded: boolean };

export default function MainMenu({ expanded }: MainMenuProps): ReactElement {
  return (
    <nav className="sidebar-nav">
      <ul className="nav nav-pills">
        {MAIN_MENU_ITEMS.map((menuItem) => (
          <MenuItemAuthoriser
            key={menuItem.to}
            menuItemState={{ menuItem, menuExpanded: expanded }}
          />
        ))}
      </ul>
    </nav>
  );
}
